import { logger } from '../utils/logger';
import { StrategyTemplate, Block } from '../types';
import { supabase } from './supabaseClient';
import { successorId, isValidSuccessor } from './templateLifecycle';

export const listTemplates = async (filters?: {
  category?: string;
  difficulty?: string;
  platform?: string;
  assetClass?: string;
  // Archived/retired templates (tagged 'archived') are hidden from normal selection. The rows are NOT
  // deleted — pass includeArchived to surface them (e.g. an admin/recovery view).
  includeArchived?: boolean;
}): Promise<StrategyTemplate[]> => {
  try {
    logger.debug('Listing templates with filters:', filters);

    let query = supabase.from('strategy_templates').select(`
        *,
        author:users(id, full_name, email)
      `);

    if (filters?.category) {
      query = query.eq('category', filters.category);
    }
    if (filters?.difficulty) {
      query = query.eq('difficulty', filters.difficulty);
    }
    if (filters?.platform) {
      query = query.eq('platform', filters.platform);
    }
    if (filters?.assetClass) {
      query = query.eq('asset_class', filters.assetClass);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    logger.debug('Templates fetched:', data?.length || 0);

    // Exclude retired (lifecycle) + archived-tagged templates from normal selection unless explicitly
    // requested. Rows are NOT deleted — includeArchived surfaces them (admin/recovery view).
    const visible = (data || []).filter((t) => {
      if (filters?.includeArchived) return true;
      const retired = (t.lifecycle_status ?? 'active') === 'retired';
      const archived = ((t.tags as string[] | null) || []).includes('archived');
      return !retired && !archived;
    });

    return visible.map((template) => ({
      id: template.id,
      name: template.name,
      description: template.description,
      blocks: template.blocks,
      platform: template.platform,
      assetClass: template.asset_class,
      created: new Date(template.created_at),
      updated: new Date(template.updated_at),
      version: template.version,
      tags: template.tags || [],
      category: template.category,
      difficulty: template.difficulty,
      timeframes: template.timeframes || [],
      marketConditions: template.market_conditions || [],
      requiredData: template.required_data || [],
      defaultParameters: template.default_parameters || {},
      author: template.author
        ? {
            id: template.author.id,
            name: template.author.full_name,
            email: template.author.email,
          }
        : undefined,
      stats: {
        downloads: template.downloads || 0,
        rating: template.rating || 0,
        reviews: template.reviews_count || 0,
      },
      isPublic: template.is_public,
      lifecycleStatus: template.lifecycle_status ?? 'active',
      migratedToTemplateId: template.migrated_to_template_id ?? null,
      retiredReason: template.retired_reason ?? null,
    }));
  } catch (error) {
    console.error('Template listing error:', error);
    throw new Error(
      error instanceof Error ? error.message : 'Failed to fetch templates'
    );
  }
};

export const getTemplate = async (id: string): Promise<StrategyTemplate> => {
  try {
    const { data, error } = await supabase
      .from('strategy_templates')
      .select(
        `
        *,
        author:users(id, full_name, email)
      `
      )
      .eq('id', id)
      .single();

    if (error) throw error;
    if (!data) throw new Error('Template not found');

    return {
      id: data.id,
      name: data.name,
      description: data.description,
      blocks: data.blocks,
      platform: data.platform,
      assetClass: data.asset_class,
      created: new Date(data.created_at),
      updated: new Date(data.updated_at),
      version: data.version,
      tags: data.tags || [],
      category: data.category,
      difficulty: data.difficulty,
      timeframes: data.timeframes || [],
      marketConditions: data.market_conditions || [],
      requiredData: data.required_data || [],
      defaultParameters: data.default_parameters || {},
      author: data.author
        ? {
            id: data.author.id,
            name: data.author.full_name,
            email: data.author.email,
          }
        : undefined,
      stats: {
        downloads: data.downloads || 0,
        rating: data.rating || 0,
        reviews: data.reviews_count || 0,
      },
      isPublic: data.is_public,
      lifecycleStatus: data.lifecycle_status ?? 'active',
      migratedToTemplateId: data.migrated_to_template_id ?? null,
      retiredReason: data.retired_reason ?? null,
    };
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : 'Failed to fetch template'
    );
  }
};

// Resolve a template id to the version that should actually LOAD into the editable builder. If the id is a
// RETIRED record with a verified migrated successor, returns the successor + `redirectedFrom` (the retired
// id) so the caller can repair persisted state + show a neutral toast. Otherwise returns the template as-is.
// Never redirects a retired template that has NO successor. Read-only/historical surfaces should call
// getTemplate directly (no redirect) to preserve the original identity.
export const resolveActiveTemplate = async (
  id: string
): Promise<{ template: StrategyTemplate | null; redirectedFrom: string | null; retiredUnresolvable?: boolean }> => {
  const loaded = await getTemplate(id);
  // Active template → render it as-is (an active legacy-no-successor record keeps its migrate banner).
  if (loaded.lifecycleStatus !== 'retired') return { template: loaded, redirectedFrom: null };
  // RETIRED → it must redirect to a VALIDATED active successor. If there is none (missing / self-ref /
  // cycle / successor-also-retired / fetch error), this is UNRESOLVABLE — signal an error so the caller
  // shows a safe error state. NEVER return the retired payload for rendering.
  const sid = successorId(loaded);
  if (sid && sid !== id) {
    try {
      const successor = await getTemplate(sid);
      if (isValidSuccessor(id, successor)) return { template: successor, redirectedFrom: id };
    } catch { /* fall through to unresolvable */ }
  }
  return { template: null, redirectedFrom: id, retiredUnresolvable: true };
};

export const createTemplate = async (
  template: Omit<StrategyTemplate, 'id' | 'created' | 'updated' | 'stats'>
): Promise<string> => {
  try {
    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('You must be logged in to create a template');
    }

    // Always use the current user's ID
    const authorId = user.id;

    logger.debug('Creating template with data:', {
      name: template.name,
      description: template.description,
      blocks: template.blocks,
      platform: template.platform,
      asset_class: template.assetClass,
      version: template.version,
      tags: template.tags,
      category: template.category,
      difficulty: template.difficulty,
      timeframes: template.timeframes,
      market_conditions: template.marketConditions,
      required_data: template.requiredData,
      default_parameters: template.defaultParameters,
      is_public: template.isPublic,
      author_id: authorId,
    });

    // S57 (2026-06-06) — `required_data` and `default_parameters` were
    // written here but never added to the schema. PostgREST rejects the
    // INSERT with [PGRST204] "Could not find the column". Removed from
    // the write path; the strategy_templates table will be extended if
    // those fields ever become product-level requirements. Read mappings
    // (line 56-57, 111-112) stay benign — they just resolve to undefined.
    const { data, error } = await supabase
      .from('strategy_templates')
      .insert({
        name: template.name,
        description: template.description,
        blocks: template.blocks,
        platform: template.platform,
        asset_class: template.assetClass,
        version: template.version,
        tags: template.tags,
        category: template.category,
        difficulty: template.difficulty,
        timeframes: template.timeframes,
        market_conditions: template.marketConditions,
        is_public: template.isPublic,
        author_id: authorId,
      })
      .select('id')
      .single();

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    return data.id;
  } catch (error) {
    console.error('Template creation error:', error);
    throw new Error(
      error instanceof Error ? error.message : 'Failed to create template'
    );
  }
};

// Helper function to serialize blocks
const serialiseBlocks = (blocks: Block[]) => JSON.parse(JSON.stringify(blocks));

export const updateTemplate = async (
  id: string,
  template: Partial<
    Omit<StrategyTemplate, 'id' | 'created' | 'updated' | 'stats'>
  >
): Promise<StrategyTemplate> => {
  try {
    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('You must be logged in to update a template');
    }

    // Build update data with only defined fields
    const updateData: Record<string, any> = {};

    if (template.name !== undefined) updateData.name = template.name;
    if (template.description !== undefined)
      updateData.description = template.description;
    if (template.blocks !== undefined)
      updateData.blocks = serialiseBlocks(template.blocks);
    if (template.platform !== undefined)
      updateData.platform = template.platform;
    if (template.assetClass !== undefined)
      updateData.asset_class = template.assetClass;
    if (template.version !== undefined) updateData.version = template.version;
    if (template.tags !== undefined) updateData.tags = template.tags;
    if (template.category !== undefined)
      updateData.category = template.category;
    if (template.difficulty !== undefined)
      updateData.difficulty = template.difficulty;
    if (template.timeframes !== undefined)
      updateData.timeframes = template.timeframes;
    if (template.marketConditions !== undefined)
      updateData.market_conditions = template.marketConditions;
    // S57 (2026-06-06) — required_data + default_parameters write paths
    // dropped (column doesn't exist; PostgREST PGRST204). See create
    // path comment above.
    if (template.isPublic !== undefined)
      updateData.is_public = template.isPublic;

    logger.debug('Updating template with ID:', id);
    logger.debug('Update data:', updateData);

    // Update the template and return the updated data
    const { data, error } = await supabase
      .from('strategy_templates')
      .update(updateData)
      .eq('id', id)
      .eq('author_id', user.id)
      .select('*')
      .single();

    if (error) {
      console.error('Update error:', error);
      throw error;
    }

    if (!data) {
      throw new Error(
        'Template not found or you do not have permission to update it'
      );
    }

    // Return the updated template
    return {
      id: data.id,
      name: data.name,
      description: data.description,
      blocks: data.blocks,
      platform: data.platform,
      assetClass: data.asset_class,
      created: new Date(data.created_at),
      updated: new Date(data.updated_at),
      version: data.version,
      tags: data.tags || [],
      category: data.category,
      difficulty: data.difficulty,
      timeframes: data.timeframes || [],
      marketConditions: data.market_conditions || [],
      requiredData: data.required_data || [],
      defaultParameters: data.default_parameters || {},
      author: { id: user.id, name: user.email || '', email: user.email },
      stats: {
        downloads: data.downloads || 0,
        rating: data.rating || 0,
        reviews: data.reviews_count || 0,
      },
      isPublic: data.is_public,
    };
  } catch (error) {
    console.error('Template update error:', error);
    throw new Error(
      error instanceof Error ? error.message : 'Failed to update template'
    );
  }
};

export const deleteTemplate = async (id: string): Promise<void> => {
  try {
    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('You must be logged in to delete a template');
    }

    const { error } = await supabase
      .from('strategy_templates')
      .delete()
      .eq('id', id)
      .eq('author_id', user.id);

    if (error) throw error;
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : 'Failed to delete template'
    );
  }
};

export const incrementDownloads = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase.rpc('increment_template_downloads', {
      template_id: id,
    });
    if (error) throw error;
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : 'Failed to increment downloads'
    );
  }
};

export const rateTemplate = async (
  id: string,
  rating: number
): Promise<void> => {
  try {
    const { error } = await supabase.rpc('rate_template', {
      template_id: id,
      rating_value: rating,
    });
    if (error) throw error;
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : 'Failed to rate template'
    );
  }
};

// Function to save a template as a strategy
export const saveTemplateAsStrategy = async (
  templateId: string,
  metadata: {
    name: string;
    description?: string;
    isPublic?: boolean;
  }
): Promise<string> => {
  try {
    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('You must be logged in to save a template as a strategy');
    }

    // Get the template
    const template = await getTemplate(templateId);

    // Create a new strategy
    const { data, error } = await supabase
      .from('strategies')
      .insert({
        user_id: user.id,
        name: metadata.name || template.name,
        description: metadata.description || template.description,
        blocks: template.blocks,
        platform: template.platform,
        asset_class: template.assetClass,
        version: template.version,
        tags: template.tags,
        is_public: metadata.isPublic || false,
      })
      .select('id')
      .single();

    if (error) throw error;

    return data.id;
  } catch (error) {
    throw new Error(
      error instanceof Error
        ? error.message
        : 'Failed to save template as strategy'
    );
  }
};

// Function to check if user is the author of a template
export const isTemplateAuthor = async (
  templateId: string
): Promise<boolean> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('strategy_templates')
      .select('author_id')
      .eq('id', templateId)
      .single();

    if (error || !data) return false;

    return data.author_id === user.id;
  } catch (error) {
    console.error('Error checking template authorship:', error);
    return false;
  }
};

// Function to create a copy of a template
export const copyTemplate = async (
  templateId: string,
  metadata: {
    name: string;
    description?: string;
    isPublic?: boolean;
  }
): Promise<string> => {
  try {
    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('You must be logged in to copy a template');
    }

    // S56 (2026-06-06) — Self-heal the public.users mirror before the
    // strategy_templates INSERT. The author_id FK references
    // public.users(id), not auth.users(id), so if the on_auth_user_created
    // trigger didn't fire (or fired before this user was created), the
    // INSERT would fail with a 23503 FK violation that the previous
    // generic "Failed to copy template" message hid. Now we upsert into
    // public.users first — no-op if the row already exists, fix-forward
    // if it doesn't. Real user case: alkhalawi.a@gmail.com signed up
    // via the S55-fixed flow but his public.users row was missing.
    const { error: mirrorErr } = await supabase
      .from('users')
      .upsert(
        { id: user.id, email: user.email ?? '' },
        { onConflict: 'id', ignoreDuplicates: false },
      );
    if (mirrorErr) {
      // The mirror upsert can fail under RLS if the policy doesn't
      // permit the caller to write their own row. We don't fail the
      // whole copy on that — fall through and let the INSERT proceed
      // (the row may already exist from a successful trigger fire);
      // worst case the user sees the same precise FK error we now
      // surface in the catch below.
      console.warn('[copyTemplate] public.users upsert non-fatal warning:', mirrorErr);
    }

    // Get the original template
    const template = await getTemplate(templateId);

    // Create a new template with the current user as author.
    // S57 (2026-06-06) — required_data + default_parameters dropped
    // (PGRST204 — column doesn't exist in strategy_templates).
    const { data, error } = await supabase
      .from('strategy_templates')
      .insert({
        name: metadata.name || `Copy of ${template.name}`,
        description: metadata.description || template.description,
        blocks: template.blocks,
        platform: template.platform,
        asset_class: template.assetClass,
        version: template.version,
        tags: template.tags,
        category: template.category,
        difficulty: template.difficulty,
        timeframes: template.timeframes,
        market_conditions: template.marketConditions,
        is_public: metadata.isPublic || false,
        author_id: user.id,
      })
      .select('id')
      .single();

    if (error) {
      // S56 — Surface the precise Postgres error code + message instead
      // of the previous generic "Failed to copy template". The
      // PostgrestError shape carries .code / .message / .details / .hint
      // that together tell us the exact failure (FK violation, RLS
      // denial, unknown column, etc.). Real user case: alkhalawi got
      // "Failed to copy template" with no diagnostic info; the actual
      // underlying error was a 23503 FK violation on author_id.
      const pgErr = error as { code?: string; message?: string; details?: string; hint?: string };
      const parts: string[] = [];
      if (pgErr.code) parts.push(`[${pgErr.code}]`);
      if (pgErr.message) parts.push(pgErr.message);
      if (pgErr.details) parts.push(`(${pgErr.details})`);
      if (pgErr.hint) parts.push(`hint: ${pgErr.hint}`);
      const full = parts.join(' ').trim() || 'unknown Postgres error';
      // Friendly translation of the two known failure modes so the user
      // sees actionable guidance, with the raw error still appended for
      // engineer debugging.
      let friendly = '';
      if (pgErr.code === '23503') {
        friendly = 'Your account is missing its public profile row — this is a one-time backfill issue. Please sign out, sign back in, and try again. (Engineer detail: ';
      } else if (pgErr.code === '42501') {
        friendly = "Permission denied by the database row-level security policy. If you're on the free tier, custom templates may be limited. (Engineer detail: ";
      } else {
        friendly = 'Copy failed at the database. (Engineer detail: ';
      }
      throw new Error(friendly + full + ')');
    }

    return data.id;
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : 'Failed to copy template'
    );
  }
};
